import React from 'react';
import { Flex, Button, Checkbox } from 'design-system-web';
import { Alert } from 'components/lds';
import routes from 'lane-shared/config/routes';
import history from 'helpers/history';
import { Channel } from 'packages/lane-shared/types/ChannelType';
import { TranslationKeys } from 'localization';
import { useTranslation } from 'react-i18next';
import { H5 } from 'components/typography';
import { useBillingPaymentSettingsForChannel } from 'lane-shared/domains/billingPayments/hooks';
import styles from './BillingTab.scss';
import { WorkOrderModuleSettings } from 'lane-shared/domains/workOrder';

import { getInvoicingOptionsEnabledValue } from '../../../../helpers/getInvoicingOptionsEnabledValue';

const TRANSLATION_KEYS: { [key: string]: TranslationKeys } = Object.freeze({
  billingPaymentsHeader:
    'web.admin.serviceRequest.settings.billing.billingPayments',
  productsServicesButton:
    'web.admin.serviceRequest.settings.billing.productsAndServicesButton',
  invoicesHeader: 'web.admin.serviceRequest.settings.billing.invoices',
  enableInvoicingCheckbox:
    'web.admin.serviceRequest.settings.billing.enableInvoicing',
  enableInvoicingDisabledWarning:
    'web.admin.serviceRequest.settings.billing.enableInvoicing.disabled.warning',
});

type Props = {
  channel: Channel;
  settings: WorkOrderModuleSettings;
  onSettingsUpdated: (isInvoiceOptionsEnabled: boolean) => void;
};

export const BillingTab = ({ channel, settings, onSettingsUpdated }: Props) => {
  const { t } = useTranslation();
  const { isInvoicingDisabled: isBillingInvoicingDisabled } =
    useBillingPaymentSettingsForChannel({
      channelId: channel._id,
    });

  return (
    <Flex direction="column" gap={7} className={styles.billingTab}>
      <Flex direction="column" align="self-start">
        <H5 className={styles.header}>
          {t(TRANSLATION_KEYS.billingPaymentsHeader)}
        </H5>
        <Button
          variant="secondary"
          size="large"
          onClick={() =>
            history.push(
              routes.channelAdminWorkOrdersServiceRequestsBillingSettings.replace(
                ':id',
                channel._id
              )
            )
          }
        >
          {t(TRANSLATION_KEYS.productsServicesButton)}
        </Button>
      </Flex>
      <Flex direction="column">
        <H5 className={styles.header}>{t(TRANSLATION_KEYS.invoicesHeader)}</H5>
        {isBillingInvoicingDisabled && (
          <Alert type="warning" className={styles.alert}>
            {t(TRANSLATION_KEYS.enableInvoicingDisabledWarning)}
          </Alert>
        )}
        <Checkbox
          selected={getInvoicingOptionsEnabledValue(
            isBillingInvoicingDisabled,
            settings?.isInvoiceOptionsEnabled
          )}
          value={getInvoicingOptionsEnabledValue(
            isBillingInvoicingDisabled,
            settings?.isInvoiceOptionsEnabled
          )}
          onChange={() =>
            onSettingsUpdated(
              !getInvoicingOptionsEnabledValue(
                isBillingInvoicingDisabled,
                settings?.isInvoiceOptionsEnabled
              )
            )
          }
          disabled={isBillingInvoicingDisabled}
          text={t(TRANSLATION_KEYS.enableInvoicingCheckbox)}
          interfaceStyle="light"
          testId="wo-invoicing-checkbox"
        />
      </Flex>
    </Flex>
  );
};
